import { useEffect, useRef, useState } from "preact/hooks";
import type { Layer, Map as LeafletMap, TileLayer } from "leaflet";

import { useIsDesktop } from "../hooks/useIsDesktop";
import { buildRainViewerTileUrlTemplate, fetchLatestRainViewerFrame } from "../lib/rainviewer";
import {
  WEATHER_RADAR_ATTRIBUTION,
  WEATHER_RADAR_DEFAULT_LAYER_CAPTION,
  WEATHER_RADAR_EXPANDED_STORAGE_KEY,
  WEATHER_RADAR_HIDE_LABEL,
  WEATHER_RADAR_MAP_LOAD_ERROR_TEXT,
  WEATHER_RADAR_PRECIP_UNAVAILABLE_TEXT,
  WEATHER_RADAR_SHOW_LABEL,
} from "../lib/weatherCopy";

export interface WeatherMapPanelProps {
  /** The farm's point (`WeatherOut.latitude`/`longitude`) — callers only
   * mount this panel once both are known non-null (see `DashboardView`'s
   * `WeatherCard`, which renders `WEATHER_RADAR_UNAVAILABLE_TEXT` instead
   * when either is null). */
  lat: number;
  lon: number;
  /** Sprint-015 C4 (weather-hub-ia-layout-2026-07-10.md §5, CD sign-off
   * Deviation 1 — BINDING) — `WeatherOut.map_base_layer`, the farm-wide
   * admin default ("map"|"satellite", set on Admin > Settings). Read-only:
   * this panel never writes it, no `/me` seam, no per-user persistence.
   * Consumed here for the `.layer-toggle-note` caption's `data-default-
   * layer` attribute (real end-to-end wiring, TD-030) — the actual
   * Map/Satellite segmented CONTROL the desktop mock also draws is
   * explicitly labeled "P3" there (a later phase, not this sprint's C4;
   * no satellite tile source is wired in this app yet, and adding one is
   * a new external-host/CSP decision outside this ticket's scope — see
   * `DashboardView.tsx`'s own C4 docstring and the sprint-015 FE report). */
  mapBaseLayer: string;
}

// ADR-weather-map-provider-2026-07-10 Phase 2 — sensible defaults: zoom
// ~8 (farm-region scale, not street-level), RainViewer precip layer on by
// default, IEM NEXRAD reflectivity an off-by-default toggle.
const DEFAULT_ZOOM = 8;
// "Auto-refresh radar frames every ~5 min while expanded."
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// Both keyless, public, browser-fetchable tile hosts (ADR "Radar / Map
// Layer Options" table) — see `app/security/headers.py` for the matching
// CSP `img-src` allowlist entries.
const OSM_TILE_URL_TEMPLATE = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const IEM_NEXRAD_WMS_URL = "https://mesonet.agron.iastate.edu/cgi-bin/wms/nexrad/n0q.cgi";

function readStoredExpanded(): boolean {
  try {
    return window.localStorage.getItem(WEATHER_RADAR_EXPANDED_STORAGE_KEY) === "true";
  } catch {
    // Private-browsing / storage-disabled — collapsed is the safe default.
    return false;
  }
}

function writeStoredExpanded(expanded: boolean): void {
  try {
    window.localStorage.setItem(WEATHER_RADAR_EXPANDED_STORAGE_KEY, expanded ? "true" : "false");
  } catch {
    // Never a hard failure — expansion just won't persist across reloads.
  }
}

/**
 * ADR-weather-map-provider-2026-07-10 Phase 2 — dashboard-only radar map.
 * Leaflet (~40 KB gz) is dynamically `import()`ed — never part of the
 * Today Card's own bundle — the FIRST time this panel expands.
 * Collapsing or unmounting tears the map AND its 5-min auto-refresh
 * interval down completely, so no background network/timer activity
 * survives a collapse (ADR "Map Rendering Approach": "degrades
 * gracefully... tiles are network-only; do not pre-cache").
 *
 * Layers: OSM base (always on) + RainViewer latest-observed-frame
 * precipitation overlay (on by default) + IEM NEXRAD reflectivity WMS
 * (off by default, toggled via Leaflet's own layer control). Required
 * attribution (OSM + RainViewer + IEM — ADR Compliance/ToS) is added to
 * the map's attribution control unconditionally, regardless of which
 * overlay is currently toggled on.
 *
 * Sprint-015 C4 (weather-hub-ia-layout-2026-07-10.md §5, CD sign-off
 * Deviation 2 — APPROVED, "the correct desktop-primary call"): the
 * Show/Hide collapse affordance stays `<1024px` only, protecting the
 * single mobile scroll column (its original motivation, unchanged
 * `localStorage`-remembered behavior below that width). At `≥1024px` the
 * radar is ALWAYS expanded — no collapse state, no toggle button
 * rendered at all — matching the desktop-primary two-column body's own
 * promotion of the radar into a real, always-visible right column.
 */
export function WeatherMapPanel({ lat, lon, mapBaseLayer }: WeatherMapPanelProps) {
  const isDesktop = useIsDesktop();
  const [storedExpanded, setStoredExpanded] = useState<boolean>(readStoredExpanded);
  // `≥1024px`: always expanded, the stored (mobile) preference never
  // consulted. `<1024px`: the unmodified, `localStorage`-remembered
  // Show/Hide state.
  const expanded = isDesktop || storedExpanded;
  const [mapLoadError, setMapLoadError] = useState(false);
  const [precipUnavailable, setPrecipUnavailable] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const rainLayerRef = useRef<TileLayer | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function toggle() {
    // Only reachable `<1024px` — the toggle button itself isn't rendered
    // at `≥1024px` (see the return below), so this never fires there.
    setStoredExpanded((prev) => {
      const next = !prev;
      writeStoredExpanded(next);
      return next;
    });
  }

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    setMapLoadError(false);
    setPrecipUnavailable(false);

    async function mount() {
      let leaflet: typeof import("leaflet");
      try {
        [leaflet] = await Promise.all([import("leaflet"), import("leaflet/dist/leaflet.css")]);
      } catch {
        if (!cancelled) setMapLoadError(true);
        return;
      }
      if (cancelled || !containerRef.current) return;
      const L = leaflet;

      const map = L.map(containerRef.current, {
        center: [lat, lon],
        zoom: DEFAULT_ZOOM,
      });
      mapRef.current = map;
      // Required attribution (ADR Compliance/ToS) — added unconditionally
      // so it's present regardless of which overlay is toggled on.
      map.attributionControl.addAttribution(WEATHER_RADAR_ATTRIBUTION);

      L.tileLayer(OSM_TILE_URL_TEMPLATE, { maxZoom: 19 }).addTo(map);

      const iemLayer = L.tileLayer.wms(IEM_NEXRAD_WMS_URL, {
        layers: "nexrad-n0q",
        format: "image/png",
        transparent: true,
      });

      const frame = await fetchLatestRainViewerFrame();
      if (cancelled) return;

      const overlays: Record<string, Layer> = { "NEXRAD reflectivity (IEM)": iemLayer };
      if (frame) {
        // Sprint-016 F4 (WX-02, discovered during the sprint-016 UI-audit
        // capture) — RainViewer's radar tile source has a documented
        // native max zoom of 7 (rainviewer.com Weather Maps API); this
        // panel's `DEFAULT_ZOOM` (8) exceeds it, so every precip tile
        // request at the default view previously hit RainViewer's own
        // "Zoom Level Not Supported" placeholder tile (verified directly
        // against `tilecache.rainviewer.com`, both 256px and 512px tile
        // sizes — the ceiling is RainViewer's, not a URL-construction
        // bug). `maxNativeZoom` tells Leaflet to fetch the zoom-7 tile and
        // auto-scale it for any deeper zoom instead of requesting a
        // zoom level RainViewer doesn't serve. The OSM base layer's own
        // `maxZoom: 19` (above) is untouched — only the precip overlay is
        // capped.
        const rainLayer = L.tileLayer(buildRainViewerTileUrlTemplate(frame), {
          opacity: 0.75,
          maxNativeZoom: 7,
        });
        rainLayer.addTo(map); // ADR default: RainViewer layer on
        rainLayerRef.current = rainLayer;
        overlays["Precipitation (RainViewer)"] = rainLayer;
      } else {
        setPrecipUnavailable(true);
      }

      L.control.layers({}, overlays).addTo(map);

      intervalRef.current = setInterval(() => {
        void (async () => {
          const latest = await fetchLatestRainViewerFrame();
          if (cancelled || !latest || !rainLayerRef.current) return;
          rainLayerRef.current.setUrl(buildRainViewerTileUrlTemplate(latest));
        })();
      }, REFRESH_INTERVAL_MS);
    }

    void mount();

    return () => {
      cancelled = true;
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      mapRef.current?.remove();
      mapRef.current = null;
      rainLayerRef.current = null;
    };
  }, [expanded, lat, lon]);

  return (
    <div class="weather-radar-panel">
      {isDesktop ? (
        // Sprint-015 C4 — the farm-wide caption replaces the Show/Hide
        // toggle at this width (CD sign-off Deviation 1, "meaning is
        // binding, exact string is a copy pass"). `data-default-layer`
        // exposes the actual consumed `map_base_layer` value for tests/a
        // future P3 toggle to hydrate from — this sprint's own C4 scope is
        // consuming the farm-wide default, not building the Map/Satellite
        // segmented control the mock separately labels "P3".
        <p class="layer-toggle-note" data-default-layer={mapBaseLayer}>
          {WEATHER_RADAR_DEFAULT_LAYER_CAPTION}
        </p>
      ) : (
        <button
          type="button"
          class="weather-radar-toggle"
          aria-expanded={expanded}
          aria-controls="weather-radar-map"
          onClick={toggle}
        >
          {expanded ? WEATHER_RADAR_HIDE_LABEL : WEATHER_RADAR_SHOW_LABEL}
        </button>
      )}
      {expanded ? (
        <>
          {mapLoadError ? (
            <p class="weather-radar-error-hint" role="alert">
              {WEATHER_RADAR_MAP_LOAD_ERROR_TEXT}
            </p>
          ) : (
            <>
              {precipUnavailable ? (
                <p class="weather-radar-error-hint" role="status">
                  {WEATHER_RADAR_PRECIP_UNAVAILABLE_TEXT}
                </p>
              ) : null}
              <div
                id="weather-radar-map"
                class="weather-radar-map"
                ref={containerRef}
                role="group"
                aria-label="Radar map centered on the farm"
              />
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
